import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { HubManagementDialogContentLayout } from "../HubManagementDialogContentLayout";
import {
	AlertBar,
	Checkbox,
	Form,
	FormattedText,
	FormItem,
	ToolbarAction,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import styles from "./CommunitiesSettings.module.less";
import {
	CommunitiesSettingsModel,
	getCommunitiesSettings,
	saveCommunitiesSettings
} from "client/Api/Communities/communitiesSettings";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { ensureNotNil } from "client/Misc/utils";
import { IValueInputAttachable } from "client/Forms/attachable";

export type CommunitiesSettingsProps = {
	onChanged: (model: CommunitiesSettingsModel) => void;
	isMobile: boolean;
	isResponsive: boolean;
};

export const CommunitiesSettings = (props: CommunitiesSettingsProps): ReactElement => {
	const {
		onChanged,
		isMobile,
		isResponsive
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);
	const [formData, setFormData] = useState<CommunitiesSettingsModel>({ isEnabled: false });
	const [lastPersistedFormData, setLastPersistedFormData] = useState<CommunitiesSettingsModel | undefined>(undefined);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const formHandler: IFormHandler<CommunitiesSettingsModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		(): (() => void) => {
			const fetch = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);
					const model: CommunitiesSettingsModel = await getCommunitiesSettings(signal);
					setLastPersistedFormData(model);
					setFormData({ ...model });
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const enableChangeHandler = useCallback(
		async (isEnabled: boolean): Promise<void> => {
			if (isEnabled) {
				return;
			}

			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToDisableCommunities"),
				children: (
					<FormattedText
						text={localize("_DisableCommunitiesConfirmationDescription")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: (c) => <b>{c}</b>
							}
						]}
					/>
				),
				confirmText: localize("_Disable"),
				trackingId: "disable-communities-confirmation-dialog"
			});

			if (result.wasSuccessful) {
				return;
			}

			const attachable: IValueInputAttachable<boolean> = formHandler.for("isEnabled");

			attachable.reducer
				.setValue(true);

			attachable.validation
				.triggerInputFinished();
		},
		[formHandler, openConfirmationDialog]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {
				const valiationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (valiationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await saveCommunitiesSettings(formData);
				setLastPersistedFormData(formData);

				alertManager.showSuccess(localize("_ChangesSaved"));
				onChanged(formData);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			onChanged,
			isBusy
		]
	);

	const cancelHandler = useCallback(
		(): void => {
			setFormData(
				ensureNotNil(lastPersistedFormData)
			);

			formHandler.validation
				.triggerInputFinished();
		},
		[formHandler.validation, lastPersistedFormData]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<void>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "save",
					title: localize("_Save"),
					icon: "floppy-disk",
					canExecute: () => true,
					execute: () => submitHandler(),
					isPrimary: true
				},
				{
					kind: ActionKind.Regular,
					name: "cancel",
					title: localize("_Cancel"),
					icon: "xmark",
					canExecute: () => true,
					execute: () => cancelHandler()
				}
			];
		},
		[cancelHandler, submitHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<void>[] => {
			return buildActionsForTopBar<void, ToolbarAction<void>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<>
			<HubManagementDialogContentLayout
				title={localize("_Settings")}
				isMobile={isMobile}
				actions={lastPersistedFormData ? topBarActions : undefined}
				actionContext={undefined}
				isResponsive={isResponsive}
				trackingId="communities-settings"
				isBusy={isBusy}
			>
				<Form onSubmit={submitHandler}>
					{!isAlertDismissed && (
						<div className={styles.alert}>
							<AlertBar
								isInline
								kind="info"
								onDismiss={() => setIsAlertDismissed(true)}
							>
								{localize("_CommunitiesSettingsInfoText")}
							</AlertBar>
						</div>
					)}

					<FormItem>
						<Checkbox
							{...formHandler.for("isEnabled")
								.attachToCheckbox()
								.withOnChange(enableChangeHandler)
							}
							text={localize("_EnableCommunities")}
						/>

						<ValidationError {...formHandler.for("isEnabled").attachToValidationError()} />
					</FormItem>
				</Form>
			</HubManagementDialogContentLayout>

			{confirmationDialog}
		</>
	);
};
CommunitiesSettings.displayName = "CommunitiesSettings";

const validationConfiguration = createValidationConfiguration<CommunitiesSettingsModel>();
